import * as React from 'react';
import moment from 'moment';

import {
  ExportChannelArchitectureAPI,
  ExportUnsavedChannelArchitectureAPI,
  ImportChannelArchitectureAPI,
} from 'api';

import LinkIcon from 'assets/icons/channel-architecture-linked-slot.svg';

import Button from 'shared/components/button/Button';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';

import { OpSchedulerNext } from 'op2mise-react-widgets/OpSchedulerNext';

import useStore from 'store/AccountStore';
import { useProgramsStore } from './store/programsStore';
import { formatDateWithMoment, addDaysToDate } from 'utils';

import { ActionTypes, ProgramTypes } from './ChannelArchitecture.d';
import { Constants } from './helper/constants';
import findRelatedBlocks from './helper/findRelatedBlocks';
import findScheduleGaps from './helper/findScheduleGaps';
import { findClonedBlock, formatStringForComparison } from './utils/architecture.utils';
import { getWeeklyEvents } from './utils/eventAdapter';

import ConfirmationModal from './modals/ConfirmationModal';
import ModifyBlock from './modals/ModifyBlock';
import { ImportComponent } from './modals/ImportComponent';
import SchedulerHeaderActions from './components/SchedulerHeaderActions';
import SchedulerDayHeader from './components/SchedulerDayHeader';

function CAProgrammes({
  channelInfo,
  dispatchSchedules,
  endDate,
  handleSaveChannelArchitecture,
  isDirty,
  numberOfWeeks,
  setIsDirty,
  setNumberOfWeeks,
  startDate,
  schedules,
  handleConstructGridData,
  headerComponent,
}) {
  const { formTypes } = Constants;
  const { user } = useStore((state) => state);
  const [openForm, setOpenForm] = React.useState(false);
  const [modifyBlock, setModifyBlock] = React.useState({
    show: false,
    blockData: null,
    type: null,
  });
  const { channelId, channelName } = channelInfo;
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDeleteBlockModal, setShowDeleteBlockModal] = React.useState({
    show: false,
    data: null,
  });
  const {
    fetchChannelArchitecture,
    fetchTitleGroupList,
    fetchSeriesList,
    programs,
    getSeriesInfo,
    getTitleGroupInfo,
    _resetPrograms,
  } = useProgramsStore((state) => state);

  const { titleGroupList, seriesList } = programs;
  const isClockTypeBroadcast = user?.clockType === 'Broadcast';
  const [isLoadingSchedules, setIsLoadingSchedules] = React.useState(false);
  const schedulerEvents = React.useMemo(
    () => getWeeklyEvents(schedules?.programmes),
    [schedules?.programmes],
  );

  // Getting date range of today and tomorrow
  // NOTE: Use addDaysToDate(x, y) from date.utils
  const tomorrow = moment().add(1, 'days').toDate();

  const period = `${formatDateWithMoment(new Date())} - ${formatDateWithMoment(tomorrow)}`;

  const getEndDate = () => {
    return numberOfWeeks > 1 ? addDaysToDate(startDate, numberOfWeeks * 7) : endDate;
  };

  const getSelectedWeek = (e) => {
    if (!e.startTime) return 1;
    const msDay = 24 * 60 * 60 * 1000; // milliseconds per day
    const totalDays = Math.floor((new Date(e.startTime) - new Date(startDate)) / msDay);
    const week = Math.floor(totalDays / 7) + 1;
    return week;
  };

  const handleCreatePlaceholders = () => {
    let gaps;
    try {
      gaps = findScheduleGaps(schedules.programmes);
    } catch (error) {
      console.error(error);
      return;
    }

    const placeholderBlocks = gaps.map((block) => ({
      ...block,
      type: ProgramTypes.PLACEHOLDER,
    }));

    if (!placeholderBlocks.length) return;

    dispatchSchedules({
      actionType: ActionTypes.UPDATEALL,
      payload: { programmes: [...schedules.programmes, ...handleConstructGridData(placeholderBlocks, false)] },
      saveChange: true,
    });
    setIsDirty(true);
  };

  const handleExportSuccess = (res) => {
    const { organisationName } = user;
    let exportDate = new Date();

    const exportedDateString =
      String(exportDate.getDate()).padStart(2, '0') +
      String(exportDate.getMonth() + 1).padStart(2, '0') +
      exportDate.getFullYear().toString();

    const fileName = `${organisationName}_op2mise_channel_architecture_programme_export_${channelName}_${exportedDateString}.xlsx`;
    const blob = new Blob([res], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, fileName);
  };

  const handleOnExport = () => {
    const allEvents = (schedules?.programmes || []).filter(
      (event) =>
        !(
          event.week === 2 &&
          findClonedBlock(event, schedules.programmes, isClockTypeBroadcast)
        ),
    );

    const payload = {
      blocks: allEvents.map((event) => ({
        availableDuration: event?.availableDuration || 0,
        blockName: event?.blockName,
        blockReference: event?.blockReference,
        channelId: channelId,
        dayOfWeek: event?.dayOfWeek,
        endTime: moment(event?.endTime, 'HH:mm:ss').format('HH:mm:ss'),
        genre: event?.genre || null,
        layout: event?.layout,
        link: event?.link ?? false,
        maxCount: event?.maxCount || 0,
        seasonID: event?.seasonID ? event.seasonID.toString() : null,
        seriesID: event?.seriesID || null,
        seriesName: event?.seriesName || null,
        sequential: false,
        startTime: moment(event?.startTime, 'HH:mm:ss').format('HH:mm:ss'),
        titleGroupID: event?.titleGroupID || null,
        titleGroupName: event?.titleGroupName || null,
        type: event?.type || 'Movies',
        week: event?.week,
      })),
    };

    if (isDirty && payload.blocks.length) {
      ExportUnsavedChannelArchitectureAPI({
        queryParams: { channelId },
        payload,
        onSuccess: handleExportSuccess,
      });
      return;
    }

    ExportChannelArchitectureAPI({
      queryParams: { channelId },
      onSuccess: handleExportSuccess,
    });
  };

  const handleOnSlotModified = ({ blockData }) => {
    const cloneData = findClonedBlock(blockData, schedules.programmes, isClockTypeBroadcast);

    setModifyBlock({
      show: true,
      blockData: {
        ...blockData,
        ...(cloneData && { cloneId: cloneData.id, cloneData }),
      },
      type: ActionTypes.UPDATE,
    });
  };

  const getBlockEntityName = (block) => {
    const type = formatStringForComparison(block.type || '');
    const entityNameByType = {
      movies: block.title || '',
      placeholder: block.blockName || '',
      series: block.seriesName || '',
      titlegroup: block.titleGroupName || '',
    };

    return entityNameByType[type] || '';
  };

  const isSameBlockEntity = (block, deletedBlock) => {
    const blockType = formatStringForComparison(block.type || '');
    const deletedBlockType = formatStringForComparison(deletedBlock.type || '');

    return (
      blockType === deletedBlockType &&
      formatStringForComparison(getBlockEntityName(block)) ===
        formatStringForComparison(getBlockEntityName(deletedBlock))
    );
  };

  const updateBlockReferences = (deletedBlocks, allEvents) => {
    const foundBlocks = allEvents.filter((block) =>
      deletedBlocks.some(
        (deletedBlock) =>
          deletedBlock.blockName === block.blockReference && isSameBlockEntity(block, deletedBlock),
      ),
    );

    return foundBlocks.map((block) => ({
      ...block,
      blockReference: '',
      link: false,
    }));
  };

  const handleDelete = (block, action) => {
    const isRepeat = block.type === 'Repeat';

    // 1. Determine the Scope of Deletion
    let blocksToDelete = [block];

    if (action === ActionTypes.MULTIPLEDELETE) {
      const relatedBlocks = findRelatedBlocks(block, schedules.programmes, {
        includeRepeats: true,
        includeSelected: false,
        requireExactName: !isRepeat,
        searchEntireSchedule: true,
      });

      const toDelete = relatedBlocks.filter((result) =>
        isRepeat ? result.type === 'Repeat' : true,
      );
      blocksToDelete = [...toDelete, block];
    }

    // 2. Handle Cloned Blocks
    // If the block is cloned, the clone must also be deleted
    const cloneData = findClonedBlock(block, schedules.programmes, isClockTypeBroadcast);
    if (cloneData) blocksToDelete.push(cloneData);

    // 3. Clear blockReference on any block that pointed at a deleted block
    const itemsToUpdate = isRepeat
      ? []
      : updateBlockReferences(
          blocksToDelete,
          schedules.programmes.filter((o) => o.type !== 'Repeat'),
        );

    // 4. Execute Final Action
    dispatchSchedules({
      actionType: ActionTypes.DELETE,
      payload: {
        idsToDelete: blocksToDelete.map((o) => o.id),
        itemsToUpdate,
      },
    });

    setIsDirty(true);
    setShowDeleteBlockModal({ show: false, data: null });
  };

  const handleCloseModifyDialog = () => {
    setModifyBlock({
      show: false,
      blockData: null,
      type: null,
    });
  };

  const handleRepeat = (args, actionType) => {
    setModifyBlock({
      show: true,
      blockData: args.event.extendedProps,
      type: actionType,
    });
  };

  const resolveSeasonName = (seriesName, seasonId) => {
    const { seriesList: _seriesList } = useProgramsStore.getState().programs;
    const seriesInfo = _seriesList.find((o) => o.name === seriesName);

    if (!seriesInfo) return;

    return seriesInfo.seasons.find((season) => Number(season.id) === parseInt(seasonId, 10));
  };

  const getReferenceList = (programmes) => {
    const rawEvents = programmes.map((e) => ({
      id: e.id,
      week: e.week,
      dayOfWeek: e.dayOfWeek,
      startTime: e.startTime,
      blockName: e.blockName,
      type: e.type,
      seriesName: e.seriesName,
      seasonID: e.seasonID,
      seasonName: e.seasonName,
      titleGroupName: e.titleGroupName,
      blockReference: e.blockReference,
    }));

    // Filter to only include events from week 1
    const weekOne = rawEvents.filter((e) => e.week < 2);

    // Sort the events by dayOfWeek and startTime
    const sorted = weekOne.sort((a, b) => {
      if (a.dayOfWeek > b.dayOfWeek) return 1;
      if (a.dayOfWeek < b.dayOfWeek) return -1;
      if (a.startTime > b.startTime) return 1;
      if (a.startTime < b.startTime) return -1;
      return 0;
    });

    // Build a list of possible link references for the user to select from when creating a new block
    return sorted.map((event) => {
      const linked = rawEvents.some((x) => x.blockReference === event.blockName);

      return {
        id: event.id,
        name: `${event.blockName} (${event.type}${linked ? ', Linked' : ''})`,
        blockName: event.blockName,
        linked,
        series: getSeriesInfo(event.seriesName, +event.seasonID),
        titleGroup: getTitleGroupInfo(event.titleGroupName),
      };
    });
  };

  const renderEventContent = (contentInfo) => {
    const blockData = contentInfo?.event?.extendedProps || {};
    const {
      startTime,
      endTime,
      link,
      blockReference,
      titleGroupName,
      seriesName,
      type,
      seasonName,
    } = blockData;

    /** Converts the date value and extracts the time */
    const getTimeString = (value) => moment(value, 'HH:mm:ss').format('HH:mm');

    const getScheduleBlockSubject = () => {
      const subjects = {
        [ProgramTypes.TITLEGROUP]: titleGroupName,
        TitleGroup: titleGroupName,
        [ProgramTypes.SERIES]: seriesName,
      };
      if (type === ProgramTypes.REPEAT) {
        return titleGroupName ?? seriesName ?? ProgramTypes.REPEAT;
      }

      return seasonName ? seasonName : (subjects[type] ?? type);
    };

    return (
      <>
        <div className="ca-event-subject">{getScheduleBlockSubject()}</div>
        <div className="op-event-content">
          <div className="op-event-schedule">{`${getTimeString(
            startTime,
          )} - ${getTimeString(endTime)}`}</div>
          <span>{type}</span>

          {link && blockReference && (
            <div className="ca-link-parent">
              {link && (
                <div className="ca-link">
                  <img className="ca-icon-link-icon" src={LinkIcon} alt="link-icon" />
                  <span className="ca-icon-link-text">{`${blockReference} ${type}`}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  const handleOnEventCreate = (dateInfo) => {
    const startTime = moment(dateInfo.dateTimestamp).format('HH:mm:ss');
    const endTime = moment(dateInfo.dateTimestamp).add(30, 'minutes').format('HH:mm:ss');

    setModifyBlock({
      show: true,
      blockData: {
        startTime,
        endTime,
        type: ProgramTypes.MOVIES,
        layout: '',
        week: getSelectedWeek({ startTime }),
      },
      type: ActionTypes.CREATE,
    });
  };

  const handleAddButtonClick = () => {
    setModifyBlock({
      show: true,
      blockData: {
        startTime: null,
        endTime: null,
        type: ProgramTypes.MOVIES,
        layout: '',
        week: 1,
      },
      type: ActionTypes.CREATE,
    });
  };

  const handleUndoClick = () => {
    if (!schedules.previous?.length) return;

    dispatchSchedules({ actionType: ActionTypes.UNDO });
    setIsDirty(true);
  };

  const fullCalendarContextMenuItems = [
    {
      key: 'delete',
      label: 'Delete',
      onClick: (args) => setShowDeleteBlockModal({ show: true, data: args.event.extendedProps }),
      iconCss: 'e-icons e-trash',
    },
    {
      key: 'modify',
      label: 'Modify',
      iconCss: 'e-icons e-edit',
      onClick: (args) => handleOnSlotModified({ blockData: args.event.extendedProps }),
    },
    {
      key: 'repeat',
      label: 'Repeat Slot',
      iconCss: 'e-icons e-repeat',
      onClick: (args) => handleRepeat(args, ActionTypes.REPEAT),
    },
    {
      key: 'multipleRepeat',
      label: 'Repeat Block',
      iconCss: 'e-icons e-repeat',
      onClick: (args) => handleRepeat(args, ActionTypes.MULTIPLEREPEAT),
    },
  ];


  React.useEffect(() => {
    // Close the dialog when the component is unmounted
    setModifyBlock({ show: false, blockData: null });

    // Clear global store when the component is unmounted to avoid stale data
    return () => {
      _resetPrograms();
    };
  }, []);

  React.useEffect(() => {
    if (channelId || isDirty) {
      setIsLoadingSchedules(true);

      // Fetch Series List and Title Group List before fetching the channel architecture blocks
      fetchSeriesList(channelId, () => {
        // Enrich series blocks with series name
        fetchChannelArchitecture(channelId, startDate, endDate, isClockTypeBroadcast, (args) => {
          const enrichedBlocks = args.map((block) => ({
            ...block,
            seasonName: resolveSeasonName(block.seriesName, block.seasonID)?.name || null,
          }));

          const payload = { programmes: enrichedBlocks };

          dispatchSchedules({
            actionType: ActionTypes.UPDATEALL,
            payload,
          });

          setIsDirty(false);
          setIsLoadingSchedules(false);
        });
      });
    }
    fetchTitleGroupList();
  }, [channelId, isClockTypeBroadcast]);

  React.useEffect(() => {
    // Again, seems to be a non-dynamically changing value
    // FIXME: This might be better as a const
    if (numberOfWeeks === 0) setNumberOfWeeks(1);
  }, [numberOfWeeks]);

  return (
    <>
      {showDeleteBlockModal.show && (
        <ConfirmationModal
          text="Do you want to delete all linked slots?"
          handleConfirm={() => handleDelete(showDeleteBlockModal.data, ActionTypes.MULTIPLEDELETE)}
          handleCloseModal={() => setShowDeleteBlockModal({ show: false, data: null })}
          handleReject={() => handleDelete(showDeleteBlockModal.data, ActionTypes.DELETE)}
          showModal={showDeleteBlockModal.show}
        />
      )}
      {openForm === 'IMPORT' && (
        <ImportComponent
          closeModal={() => setOpenForm(false)}
          setIsDirty={setIsDirty}
          scheduleInfo={{ ...channelInfo, period }}
          schedulerData={{
            schedules: schedules.programmes,
            period: { startTime: startDate, endTime: getEndDate() },
            scheduleInfo: {
              ...channelInfo,
              period,
            },
            seriesList,
            titleGroupList,
          }}
          sampleTemplate="programmeImportTemplate"
          handleOnImportApi={ImportChannelArchitectureAPI}
          dataExists="channelArchitectureExist"
          dataModel="channelArchitectureModel"
          importResultColumns={[
            {
              field: 'dayOfWeekValue',
              headerText: 'Day of Week',
              maxWidth: 120,
            },
            {
              field: 'startTime',
              headerText: 'Start Time',
              maxWidth: 100,
            },
            {
              field: 'endTime',
              headerText: 'End Time',
              maxWidth: 100,
            },
            {
              field: 'type',
              headerText: 'Type',
              maxWidth: 110,
            },
            {
              field: 'genre',
              headerText: 'Genre',
              maxWidth: 80,
            },
          ]}
          importResultFileTitle="programme_import_result"
          setSchedules={(data) => {
            if (data.length) {
              const importedData = handleConstructGridData(data, false);
              dispatchSchedules({
                actionType: ActionTypes.UPDATEALL,
                payload: { programmes: importedData },
                saveChange: true,
              });
              setIsLoading(false);
              setIsDirty(true);
            }
          }}
        />
      )}
      {channelId === 0 && !channelName ? null : isLoading ? (
        <div style={{ height: 'calc(100vh - 120px)' }}>
          <BootstrapSpinner />
        </div>
      ) : (
        <>
          <OpSchedulerNext
            contextMenu={{
              items: fullCalendarContextMenuItems,
            }}
            dayHeaderContent={(args) => <SchedulerDayHeader {...args} />}
            events={schedulerEvents}
            eventContent={renderEventContent}
            eventCreateEnabled={false}
            eventResizableFromStart={false}
            eventDurationEditable={false}
            eventStartEditable={false}
            eventUpdateEnabled={false}
            firstDay={1}
            header={{
              left: () => headerComponent,
              right: () => (
                <SchedulerHeaderActions
                  onAdd={handleAddButtonClick}
                  onUndo={handleUndoClick}
                  canUndo={Boolean(schedules.previous?.length)}
                  onImport={() => setOpenForm(formTypes.import)}
                  onExport={handleOnExport}
                />
              ),
            }}
            initialView="channelArchitectureView"
            isBroadcastClock={isClockTypeBroadcast}
            isLoading={isLoadingSchedules}
            onDateDoubleClick={handleOnEventCreate}
            tooltip="off"
            views={{
              channelArchitectureView: {
                type: 'timeGridWeek',
                duration: { days: 7 },
                dateAlignment: 'week',
                firstDay: 1,
              },
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
            <Button text="Placeholders" onClick={handleCreatePlaceholders} />
            <Button
              text="Save"
              style={{ marginLeft: '10px' }}
              onClick={() => handleSaveChannelArchitecture(schedules.programmes)}
              disabled={!isDirty}
            />
          </div>
        </>
      )}

      {modifyBlock.show && (
        <ModifyBlock
          schedules={
            schedules.programmes?.length
              ? {
                  programmes: schedules.programmes?.filter(
                    (schedule) => schedule.week === 1 || schedule.week === 0,
                  ),
                }
              : { programmes: [] }
          }
          blockData={modifyBlock.blockData}
          dispatchSchedules={dispatchSchedules}
          isBroadcast={isClockTypeBroadcast}
          modalType={modifyBlock.type}
          numberOfWeeks={numberOfWeeks}
          referenceList={getReferenceList(schedules?.programmes || [])}
          handleCloseModal={handleCloseModifyDialog}
          setIsDirty={setIsDirty}
        />
      )}
    </>
  );
}

export default CAProgrammes;
